import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Phone, Play } from 'lucide-react';
import { pickText, pickPlain } from '../lib/content';

export const HeroSection = ({ t, content, lang = 'sq', onNavigate }) => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const c = content?.hero || {};
  const heroTitle = pickText(c.title, lang, t.hero.title);
  const heroSubtitle = pickText(c.subtitle, lang, t.hero.subtitle);
  const heroCta = pickText(c.cta, lang, t.hero.cta);
  const heroCtaSecondary = pickText(c.ctaSecondary, lang, t.hero.ctaSecondary);
  const heroBadge = pickText(c.badge, lang, t.agent.experience);
  const backgroundImage = pickPlain(c.background_image, "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80");
  const featuredImage = pickPlain(c.featured_image, "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80");
  const featuredBadge = pickText(c.featured_badge, lang, "Featured Property");
  const featuredTitle = pickText(c.featured_title, lang, "Luxury Villa");
  const featuredLocation = pickText(c.featured_location, lang, "Prishtinë, Kosovë");
  const featuredBedrooms = pickPlain(c.featured_bedrooms, "5");
  const featuredBathrooms = pickPlain(c.featured_bathrooms, "4");
  const featuredArea = pickPlain(c.featured_area, "280");
  const stat1v = pickPlain(c.stat1_value, "20+");
  const stat1l = pickText(c.stat1_label, lang, t.about.experience);
  const stat2v = pickPlain(c.stat2_value, "500+");
  const stat2l = pickText(c.stat2_label, lang, t.about.properties);
  const stat3v = pickPlain(c.stat3_value, "1000+");
  const stat3l = pickText(c.stat3_label, lang, t.about.clients);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const parallaxOffset = scrollY * 0.5;
  const mouseParallaxX = (mousePosition.x - window.innerWidth / 2) * 0.02;
  const mouseParallaxY = (mousePosition.y - window.innerHeight / 2) * 0.02;

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Video Background Layer */}
      <div 
        className="absolute inset-0 z-0"
        style={{ transform: `translateY(${parallaxOffset}px)` }}
      >
        {/* Placeholder for video - will be replaced with actual video */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-black to-slate-900">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("${backgroundImage}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: `scale(1.1) translate(${mouseParallaxX}px, ${mouseParallaxY}px)`,
              transition: 'transform 0.3s ease-out'
            }}
          />
        </div>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>
        
        {/* Animated gradient orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#C5A05E]/30 rounded-full blur-3xl animate-pulse" 
          style={{ animationDuration: '4s', transform: `translate(${mouseParallaxX * 2}px, ${mouseParallaxY * 2}px)` }} />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#C5A05E]/20 rounded-full blur-3xl animate-pulse" 
          style={{ animationDuration: '6s', animationDelay: '2s', transform: `translate(${-mouseParallaxX * 2}px, ${-mouseParallaxY * 2}px)` }} />
      </div>

      {/* Geometric decorative elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 border border-[#C5A05E]/20 rotate-45 animate-spin" 
          style={{ animationDuration: '20s', transform: `translate(${mouseParallaxX * 3}px, ${mouseParallaxY * 3}px) rotate(45deg)` }} />
        <div className="absolute bottom-40 right-20 w-48 h-48 border-2 border-white/10 rounded-full" 
          style={{ transform: `translate(${-mouseParallaxX * 2}px, ${-mouseParallaxY * 2}px)` }} />
        <div className="absolute top-1/2 right-10 w-24 h-24 border border-[#C5A05E]/30 animate-pulse" 
          style={{ animationDuration: '3s' }} />
      </div>

      {/* Main Content - Elevated Layer */}
      <div 
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32"
        style={{ transform: `translateY(${parallaxOffset * 0.3}px)` }}
      >
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="space-y-8">
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#C5A05E]/20 to-transparent backdrop-blur-xl border border-[#C5A05E]/30 rounded-2xl transform hover:scale-105 transition-transform duration-300">
              <div className="relative">
                <div className="w-3 h-3 bg-[#C5A05E] rounded-full animate-ping absolute" />
                <div className="w-3 h-3 bg-[#C5A05E] rounded-full" />
              </div>
              <span className="text-white/90 text-sm font-semibold tracking-wider uppercase">{heroBadge}</span>
            </div>

            {/* Main Title - Staggered Animation */}
            <div className="space-y-4">
              <h1 className="text-6xl md:text-8xl font-bold text-white leading-none">
                <span className="inline-block animate-slideInLeft" style={{ animationDelay: '0.1s', opacity: 0, animationFillMode: 'forwards' }}>
                  {heroTitle.split(' ')[0]}
                </span>{' '}
                <br />
                <span className="inline-block animate-slideInLeft bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] bg-clip-text text-transparent" 
                  style={{ animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards' }}>
                  {heroTitle.split(' ').slice(1).join(' ')}
                </span>
              </h1>
            </div>

            {/* Subtitle with glass effect */}
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed max-w-xl backdrop-blur-sm bg-white/5 p-6 rounded-2xl border border-white/10 animate-fadeInUp" 
              style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}>
              {heroSubtitle}
            </p>

            {/* CTA Buttons - Bold animations */}
            <div className="flex flex-col sm:flex-row items-start gap-4 animate-fadeInUp" 
              style={{ animationDelay: '0.7s', opacity: 0, animationFillMode: 'forwards' }}>
              <Button
                onClick={() => onNavigate('properties')}
                size="lg"
                className="relative overflow-hidden bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white px-10 py-7 text-lg font-bold rounded-2xl shadow-2xl shadow-[#C5A05E]/50 transition-all duration-500 hover:scale-110 hover:shadow-[#C5A05E]/70 group border-2 border-[#C5A05E]/50"
              >
                <span className="relative z-10 flex items-center">
                  {heroCta}
                  <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </Button>
              <Button
                onClick={() => onNavigate('contact')}
                size="lg"
                className="relative overflow-hidden backdrop-blur-xl bg-white/10 hover:bg-white/20 text-white px-10 py-7 text-lg font-bold rounded-2xl border-2 border-white/30 transition-all duration-300 hover:scale-105 hover:border-white/50 group"
              >
                <Phone className="mr-3 w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
                {heroCtaSecondary}
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-6 pt-8 animate-fadeInUp" 
              style={{ animationDelay: '0.9s', opacity: 0, animationFillMode: 'forwards' }}>
              {[
                { value: stat1v, label: stat1l },
                { value: stat2v, label: stat2l },
                { value: stat3v, label: stat3l }
              ].map((stat, index) => (
                <div key={index} className="text-center group cursor-pointer">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400 mt-2 group-hover:text-[#C5A05E] transition-colors duration-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - 3D Property Card */}
          <div className="relative animate-fadeInUp" style={{ animationDelay: '1s', opacity: 0, animationFillMode: 'forwards' }}>
            <div 
              className="relative group"
              style={{ transform: `perspective(1000px) rotateY(${mouseParallaxX * 0.5}deg) rotateX(${-mouseParallaxY * 0.5}deg)` }}
            >
              {/* Glowing effect behind card */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#C5A05E] to-transparent rounded-3xl blur-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
              
              {/* Main Property Card */}
              <div className="relative backdrop-blur-2xl bg-white/10 rounded-3xl border border-white/20 overflow-hidden shadow-2xl transform group-hover:scale-105 transition-all duration-500">
                <div className="relative h-[500px]">
                  <img
                    src={featuredImage}
                    alt="Featured Property"
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  
                  {/* Play button for video */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md border-2 border-white/50 flex items-center justify-center hover:scale-110 hover:bg-white/30 transition-all duration-300 group/play">
                      <Play className="w-8 h-8 text-white ml-1 group-hover/play:scale-110 transition-transform" fill="white" />
                    </button>
                  </div>
                  
                  {/* Property Info at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="space-y-3">
                      <div className="inline-block px-4 py-2 bg-[#C5A05E] rounded-full text-white text-sm font-bold">
                        {featuredBadge}
                      </div>
                      <h3 className="text-3xl font-bold text-white">{featuredTitle}</h3>
                      <p className="text-gray-300 text-lg">{featuredLocation}</p>
                      <div className="flex items-center gap-6 text-white/80">
                        <span>{featuredBedrooms} <span className="text-sm">{lang === 'sq' ? 'Dhoma' : 'Bedrooms'}</span></span>
                        <span>•</span>
                        <span>{featuredBathrooms} <span className="text-sm">{lang === 'sq' ? 'Banjo' : 'Bathrooms'}</span></span>
                        <span>•</span>
                        <span>{featuredArea} m²</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator - Animated */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="flex flex-col items-center gap-2 cursor-pointer group" onClick={() => onNavigate('about')}>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2 group-hover:border-[#C5A05E] transition-colors">
            <div className="w-1.5 h-3 bg-[#C5A05E] rounded-full animate-scroll" />
          </div>
          <span className="text-white/50 text-xs uppercase tracking-wider group-hover:text-[#C5A05E] transition-colors">Scroll</span>
        </div>
      </div>
    </section>
  );
};